/**
 * NEXUS UI - LogViewer
 * Terminal-style scrolling log viewer
 */

import React, { useRef, useEffect } from 'react';
import type { BaseProps, NexusVariant } from './types';

type LogLevel = 'info' | 'success' | 'warning' | 'error' | 'debug';

interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  source?: string;
}

export interface LogViewerProps extends BaseProps {
  logs: LogEntry[];
  maxHeight?: number;
  autoScroll?: boolean;
  showTimestamp?: boolean;
  showLevel?: boolean;
  showSource?: boolean;
  monospace?: boolean;
  variant?: NexusVariant;
  onLogClick?: (log: LogEntry) => void;
}

const levelColors: Record<LogLevel, { color: string; bg: string; label: string }> = {
  info: { color: '#00d4ff', bg: 'rgba(0, 212, 255, 0.1)', label: 'INFO' },
  success: { color: '#00ff88', bg: 'rgba(0, 255, 136, 0.1)', label: 'OK' },
  warning: { color: '#ffaa00', bg: 'rgba(255, 170, 0, 0.1)', label: 'WARN' },
  error: { color: '#ff3366', bg: 'rgba(255, 51, 102, 0.1)', label: 'ERR' },
  debug: { color: '#a855f7', bg: 'rgba(168, 85, 247, 0.1)', label: 'DBG' },
};

export const LogViewer: React.FC<LogViewerProps> = ({
  logs,
  maxHeight = 300,
  autoScroll = true,
  showTimestamp = true,
  showLevel = true,
  showSource = true,
  monospace = true,
  onLogClick,
  className = '',
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevLogCountRef = useRef(logs.length);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && containerRef.current && logs.length > prevLogCountRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
    prevLogCountRef.current = logs.length;
  }, [logs.length, autoScroll]);

  return (
    <div
      className={`nx-log-viewer ${className}`}
      style={{
        background: 'var(--nx-void-deep)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '4px',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'var(--nx-void-elevated)',
          borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#00ff88',
              boxShadow: '0 0 8px rgba(0, 255, 136, 0.6)',
            }}
          />
          <span
            style={{
              fontFamily: 'var(--nx-font-interface)',
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--nx-text-tertiary)',
            }}
          >
            Live Log
          </span>
        </div>
        <span
          style={{
            fontFamily: 'var(--nx-font-mono)',
            fontSize: '10px',
            color: 'var(--nx-text-ghost)',
          }}
        >
          {logs.length} entries
        </span>
      </div>

      {/* Log entries */}
      <div
        ref={containerRef}
        style={{
          maxHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
        }}
      >
        {logs.length === 0 ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: 'var(--nx-text-ghost)',
              fontFamily: 'var(--nx-font-interface)',
              fontSize: '12px',
            }}
          >
            No log entries
          </div>
        ) : (
          logs.map((log, index) => {
            const level = levelColors[log.level];

            return (
              <div
                key={log.id}
                onClick={() => onLogClick?.(log)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '6px 12px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.03)',
                  cursor: onLogClick ? 'pointer' : 'default',
                  transition: 'background 0.1s ease-out',
                  animation: index === logs.length - 1 ? 'nx-pulse 0.3s ease-out' : 'none',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {/* Timestamp */}
                {showTimestamp && (
                  <span
                    style={{
                      flexShrink: 0,
                      fontFamily: 'var(--nx-font-mono)',
                      fontSize: '10px',
                      color: 'var(--nx-text-ghost)',
                      minWidth: '70px',
                    }}
                  >
                    {log.timestamp}
                  </span>
                )}

                {/* Level badge */}
                {showLevel && (
                  <span
                    style={{
                      flexShrink: 0,
                      fontFamily: 'var(--nx-font-mono)',
                      fontSize: '9px',
                      fontWeight: 600,
                      color: level.color,
                      background: level.bg,
                      padding: '2px 6px',
                      borderRadius: '2px',
                      minWidth: '36px',
                      textAlign: 'center',
                    }}
                  >
                    {level.label}
                  </span>
                )}

                {/* Source */}
                {showSource && log.source && (
                  <span
                    style={{
                      flexShrink: 0,
                      fontFamily: 'var(--nx-font-mono)',
                      fontSize: '10px',
                      color: 'var(--nx-text-tertiary)',
                      maxWidth: '80px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    [{log.source}]
                  </span>
                )}

                {/* Message */}
                <span
                  style={{
                    flex: 1,
                    fontFamily: monospace ? 'var(--nx-font-mono)' : 'var(--nx-font-interface)',
                    fontSize: '12px',
                    color: log.level === 'error' ? level.color : 'var(--nx-text-secondary)',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                  }}
                >
                  {log.message}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Footer with gradient fade */}
      <div
        style={{
          position: 'relative',
          height: '4px',
          background: `linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent)`,
        }}
      />
    </div>
  );
};

export default LogViewer;
